import { useState } from 'react'
import {
  Instagram as InstagramIcon, Users, UserCheck, Image, Heart, MessageCircle,
  Loader2, Plus, Trash2, Pencil, Check, X, RefreshCw, Send,
} from 'lucide-react'
import { useLanguage } from '@/i18n/LanguageContext'
import {
  useV1Accounts, useIGProfile, useIGMedia, useIGComments, useAddIGComment, useEditIGComment, useDeleteIGComment,
  useDMReplyToComment,
  type IGMedia,
} from '@/api/hooks/useInstagram'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { toast } from 'sonner'
import { useQueryClient } from '@tanstack/react-query'

const fmtNum = new Intl.NumberFormat('en-US')

function formatDate(ts: string) {
  return new Date(ts).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' })
}

export function Instagram() {
  const { t } = useLanguage()
  const { data: accounts, isLoading: loadingAccounts } = useV1Accounts()
  const [selectedAccount, setSelectedAccount] = useState<string>('')
  const [selectedMedia, setSelectedMedia] = useState<IGMedia | null>(null)

  const igAccounts = (accounts || []).filter(a => a.igUserId && !a.fbOnly)

  const igUserId = selectedAccount || igAccounts[0]?.igUserId || ''
  const { data: profile, isLoading: loadingProfile } = useIGProfile(igUserId || undefined)
  const { data: mediaData, isLoading: loadingMedia } = useIGMedia(igUserId || undefined)

  if (loadingAccounts) {
    return (
      <div className="space-y-6 p-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-40 w-full" />
      </div>
    )
  }

  if (igAccounts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-20 text-muted-foreground">
        <InstagramIcon className="h-12 w-12" />
        <p className="text-lg font-medium">{t('Nenhuma conta Instagram conectada')}</p>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <InstagramIcon className="h-6 w-6 text-pink-500" />
          <h1 className="text-xl font-bold tracking-tight">{t('Instagram')}</h1>
        </div>
        {igAccounts.length > 1 && (
          <Select value={igUserId} onValueChange={setSelectedAccount}>
            <SelectTrigger className="w-[240px]">
              <SelectValue placeholder={t('Selecionar conta')} />
            </SelectTrigger>
            <SelectContent>
              {igAccounts.map(a => (
                <SelectItem key={a.igUserId} value={a.igUserId}>
                  @{a.username || a.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Profile Card */}
      {loadingProfile ? (
        <Card className="p-6">
          <div className="flex items-center gap-6">
            <Skeleton className="h-20 w-20 rounded-full" />
            <div className="space-y-2">
              <Skeleton className="h-6 w-32" />
              <Skeleton className="h-4 w-48" />
              <Skeleton className="h-4 w-64" />
            </div>
          </div>
        </Card>
      ) : profile ? (
        <Card className="p-6">
          <div className="flex flex-col gap-6 sm:flex-row sm:items-center">
            <Avatar className="h-20 w-20">
              <AvatarImage src={profile.profile_picture_url} alt={profile.username} />
              <AvatarFallback className="text-2xl">{(profile.username || 'I')[0].toUpperCase()}</AvatarFallback>
            </Avatar>
            <div className="flex-1 space-y-2">
              <div className="flex items-center gap-3">
                <h2 className="text-xl font-bold">{profile.name}</h2>
                <Badge variant="secondary">@{profile.username}</Badge>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="outline" className="text-[9px] font-mono px-1.5 py-0">IG User ID: {profile.id}</Badge>
                <Badge variant="outline" className="text-[9px] font-mono px-1.5 py-0">API: GET /{profile.id}?fields=username,name,biography,followers_count,media_count</Badge>
              </div>
              {profile.biography && (
                <p className="text-sm text-muted-foreground max-w-lg">{profile.biography}</p>
              )}
              <div className="flex gap-6 pt-2">
                <div className="flex items-center gap-2">
                  <Image className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-semibold">{fmtNum.format(profile.media_count)}</span>
                  <span className="text-xs text-muted-foreground">{t('Posts')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-semibold">{fmtNum.format(profile.followers_count)}</span>
                  <span className="text-xs text-muted-foreground">{t('Seguidores')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <UserCheck className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-semibold">{fmtNum.format(profile.follows_count)}</span>
                  <span className="text-xs text-muted-foreground">{t('Seguindo')}</span>
                </div>
              </div>
            </div>
          </div>
        </Card>
      ) : null}

      {/* Media Grid */}
      <div>
        <h3 className="text-lg font-semibold mb-4">{t('Midias Recentes')}</h3>
        {loadingMedia ? (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="aspect-square rounded-lg" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
            {(mediaData?.data || []).slice(0, 5).map(media => (
              <button
                key={media.id}
                onClick={() => setSelectedMedia(media)}
                className="group relative aspect-square overflow-hidden rounded-lg border bg-muted transition-all hover:ring-2 hover:ring-primary"
              >
                <img
                  src={media.thumbnail_url || media.media_url}
                  alt={media.caption || 'Instagram media'}
                  className="h-full w-full object-cover"
                />
                {media.media_type === 'VIDEO' && (
                  <Badge className="absolute top-2 left-2 text-[10px]" variant="secondary">VIDEO</Badge>
                )}
                {media.media_type === 'CAROUSEL_ALBUM' && (
                  <Badge className="absolute top-2 left-2 text-[10px]" variant="secondary">ALBUM</Badge>
                )}
                <div className="absolute inset-0 flex items-center justify-center gap-4 bg-black/50 opacity-0 transition-opacity group-hover:opacity-100">
                  <div className="flex items-center gap-1 text-white">
                    <Heart className="h-4 w-4 fill-white" />
                    <span className="text-sm font-semibold">{fmtNum.format(media.like_count)}</span>
                  </div>
                  <div className="flex items-center gap-1 text-white">
                    <MessageCircle className="h-4 w-4 fill-white" />
                    <span className="text-sm font-semibold">{fmtNum.format(media.comments_count)}</span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Comments Dialog */}
      {selectedMedia && (
        <CommentsDialog
          igUserId={igUserId}
          media={selectedMedia}
          onClose={() => setSelectedMedia(null)}
        />
      )}
    </div>
  )
}

function CommentsDialog({
  igUserId,
  media,
  onClose,
}: {
  igUserId: string
  media: IGMedia
  onClose: () => void
}) {
  const { t } = useLanguage()
  const qc = useQueryClient()
  const { data: commentsData, isLoading } = useIGComments(igUserId, media.id)
  const addComment = useAddIGComment()
  const editComment = useEditIGComment()
  const deleteComment = useDeleteIGComment()
  const dmReply = useDMReplyToComment()
  const [newComment, setNewComment] = useState('')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editText, setEditText] = useState('')
  const [dmCommentId, setDmCommentId] = useState<string | null>(null)
  const [dmText, setDmText] = useState('')

  const handleAdd = async () => {
    if (!newComment.trim()) return
    try {
      await addComment.mutateAsync({ igUserId, mediaId: media.id, message: newComment.trim() })
      setNewComment('')
      toast.success(t('Comentario adicionado!'))
      qc.invalidateQueries({ queryKey: ['ig-comments', igUserId, media.id] })
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('Erro ao adicionar comentario'))
    }
  }

  const handleEdit = async (commentId: string) => {
    if (!editText.trim()) return
    try {
      await editComment.mutateAsync({ igUserId, commentId, message: editText.trim() })
      setEditingId(null)
      setEditText('')
      toast.success(t('Comentario editado!'))
      qc.invalidateQueries({ queryKey: ['ig-comments', igUserId, media.id] })
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('Erro ao editar comentario'))
    }
  }

  const handleDelete = async (commentId: string) => {
    try {
      await deleteComment.mutateAsync({ igUserId, commentId })
      toast.success(t('Comentario removido!'))
      qc.invalidateQueries({ queryKey: ['ig-comments', igUserId, media.id] })
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('Erro ao remover comentario'))
    }
  }

  const handleDMReply = async (commentId: string) => {
    if (!dmText.trim()) return
    try {
      const result = await dmReply.mutateAsync({ igUserId, commentId, message: dmText.trim() })
      setDmCommentId(null)
      setDmText('')
      toast.success(`DM sent via POST /${igUserId}/messages — Recipient: ${result.recipientId || 'OK'}`, { duration: 6000 })
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('Erro ao enviar DM'))
    }
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageCircle className="h-5 w-5" />
            {t('Comentarios')}
          </DialogTitle>
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="outline" className="text-[9px] font-mono px-1.5 py-0">Media ID: {media.id}</Badge>
            <Badge variant="outline" className="text-[9px] font-mono px-1.5 py-0">API: GET /{igUserId}/comments/{media.id}</Badge>
            <Badge variant="outline" className="text-[9px] font-mono px-1.5 py-0">Permission: instagram_manage_comments</Badge>
          </div>
        </DialogHeader>

        {/* Media preview */}
        <div className="flex gap-3 border-b pb-3">
          <img
            src={media.thumbnail_url || media.media_url}
            alt=""
            className="h-16 w-16 rounded-lg object-cover"
          />
          <div className="flex-1 min-w-0">
            <p className="text-sm text-muted-foreground line-clamp-2">{media.caption || '—'}</p>
            <div className="flex gap-3 mt-1 text-xs text-muted-foreground">
              <span>{formatDate(media.timestamp)}</span>
              <span className="flex items-center gap-1"><Heart className="h-3 w-3" /> {media.like_count}</span>
              <span className="flex items-center gap-1"><MessageCircle className="h-3 w-3" /> {media.comments_count}</span>
            </div>
          </div>
        </div>

        {/* Add comment */}
        <div className="flex gap-2">
          <Input
            value={newComment}
            onChange={e => setNewComment(e.target.value)}
            placeholder={t('Escrever comentario...')}
            onKeyDown={e => e.key === 'Enter' && handleAdd()}
          />
          <Button
            size="sm"
            onClick={handleAdd}
            disabled={addComment.isPending || !newComment.trim()}
          >
            {addComment.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          </Button>
        </div>

        {/* Comments list */}
        <div className="flex-1 overflow-y-auto space-y-3 min-h-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (commentsData?.data || []).length === 0 ? (
            <p className="text-center text-sm text-muted-foreground py-8">{t('Nenhum comentario')}</p>
          ) : (
            (commentsData?.data || []).map(comment => (
              <div key={comment.id} className="flex items-start gap-3 rounded-lg border p-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold">{comment.from?.username || 'Unknown'}</span>
                    {comment.from?.id && (
                      <Badge variant="outline" className="text-[9px] font-mono px-1.5 py-0">
                        User ID: {comment.from.id}
                      </Badge>
                    )}
                    <Badge variant="outline" className="text-[9px] font-mono px-1.5 py-0">
                      Comment ID: {comment.id}
                    </Badge>
                    <span className="text-xs text-muted-foreground">{formatDate(comment.timestamp)}</span>
                  </div>
                  {editingId === comment.id ? (
                    <div className="flex gap-2 mt-1">
                      <Input
                        value={editText}
                        onChange={e => setEditText(e.target.value)}
                        className="h-8 text-sm"
                        onKeyDown={e => e.key === 'Enter' && handleEdit(comment.id)}
                        autoFocus
                      />
                      <Button
                        size="icon"
                        className="h-8 w-8 shrink-0"
                        onClick={() => handleEdit(comment.id)}
                        disabled={editComment.isPending || !editText.trim()}
                      >
                        {editComment.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 shrink-0"
                        onClick={() => { setEditingId(null); setEditText('') }}
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ) : (
                    <p className="text-sm mt-1">{comment.text}</p>
                  )}
                  {comment.like_count > 0 && (
                    <span className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                      <Heart className="h-3 w-3" /> {comment.like_count}
                    </span>
                  )}
                </div>
                {editingId !== comment.id && (
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-blue-500 hover:text-blue-600"
                      title={t('Enviar DM')}
                      onClick={() => { setDmCommentId(dmCommentId === comment.id ? null : comment.id); setDmText('') }}
                    >
                      <Send className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground hover:text-foreground"
                      onClick={() => { setEditingId(comment.id); setEditText(comment.text) }}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-destructive hover:text-destructive"
                      onClick={() => handleDelete(comment.id)}
                      disabled={deleteComment.isPending}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                )}
                {dmCommentId === comment.id && (
                  <div className="flex gap-2 mt-2 w-full">
                    <Input
                      value={dmText}
                      onChange={e => setDmText(e.target.value)}
                      placeholder={t('Mensagem para') + ` @${comment.from?.username || '?'}...`}
                      className="h-8 text-sm"
                      onKeyDown={e => e.key === 'Enter' && handleDMReply(comment.id)}
                      autoFocus
                    />
                    <Button
                      size="sm"
                      className="h-8 shrink-0"
                      onClick={() => handleDMReply(comment.id)}
                      disabled={dmReply.isPending || !dmText.trim()}
                    >
                      {dmReply.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                    </Button>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
